import {Button, Col, Form, Input, message, Modal, Row, Space, Table, Tag, Tooltip} from 'antd';
import {CheckOutlined, CloseOutlined, ExclamationCircleOutlined, SearchOutlined} from "@ant-design/icons";
import {IDENTIFICATION_SOURCE, NOT_AVAILABLE, YYYY_MM_DD_HH_MM} from "../../../util/Constants";
import React, {useEffect, useRef, useState} from "react";
import adminService from "../../../service/adminService";
import {
    MSG_INDIVIDUAL_ACCOUNT_REQUEST_ACCEPT_FAILURE_ERROR,
    MSG_INDIVIDUAL_ACCOUNT_REQUEST_ACCEPT_SUCCESS,
    MSG_INDIVIDUAL_ACCOUNT_REQUEST_REJECT_FAILURE_ERROR,
    MSG_INDIVIDUAL_ACCOUNT_REQUEST_REJECT_SUCCESS,
} from "../../../util/Messages";
import ErrorHandler from "../../../util/ErrorHandler";
import modal from 'antd/es/modal';
import dayjs from "dayjs";
import {FilterConfirmProps} from "antd/es/table/interface";
import Highlighter from 'react-highlight-words';
import {InputRef} from 'antd/es/input';
import {IndividualAccountWrapperDto} from "../../../dto/IndividualAccountWrapperDto";
import TextArea from "antd/es/input/TextArea";

interface IndividualAccountRequestTableProps {
    tableData: IndividualAccountWrapperDto[],
    tableUpdateFunc: (data: boolean) => void,
}

enum RecordStatus {
    ACCEPTED = "ACCEPTED",
    REJECTED = "REJECTED"

}

type DataIndex = keyof IndividualAccountWrapperDto;
const IndividualAccountRequestTable = (props: IndividualAccountRequestTableProps) => {
    const [tableData, setTableData] = useState<IndividualAccountWrapperDto[]>();
    const [selectedData, setSelectedData] = useState<IndividualAccountWrapperDto>();
    const [searchText, setSearchText] = useState('');
    const [searchedColumn, setSearchedColumn] = useState('');
    const [isTableLoading, setTableLoading] = useState<boolean>(false);
    const [isRejectReasonModalOpen, setRejectReasonModalOpen] = useState<boolean>(false);
    const searchInput = useRef<InputRef>();

    const handleSearch = (
        selectedKeys: string[],
        confirm: (param?: FilterConfirmProps) => void,
        dataIndex: DataIndex,
    ) => {
        confirm();
        setSearchText(selectedKeys[0]);
        setSearchedColumn(dataIndex);
    };

    const handleReset = (clearFilters: () => void, dataIndex: DataIndex) => {
        clearFilters();
        setSearchText('');
        setSearchedColumn(dataIndex);
    };
    useEffect(() => {
        setTableData(props.tableData)
    }, [props.tableData])

    const getColumnSearchProps = (dataIndex: DataIndex, columnName: string): {
        filterDropdown: ({
                             setSelectedKeys,
                             selectedKeys,
                             confirm,
                             clearFilters,
                             close
                         }: {
            setSelectedKeys: any;
            selectedKeys: any;
            confirm: any;
            clearFilters: any;
            close: any
        }) => JSX.Element;
        filterIcon: (filtered: boolean) => JSX.Element;
        onFilter: (value: any, record: any) => boolean;
        render: (text: any) => any;
        onFilterDropdownOpenChange: (visible: any) => void
    } => ({
        filterDropdown: ({setSelectedKeys, selectedKeys, confirm, clearFilters, close}) => (
            <div className='table-column-search' onKeyDown={(e) => e.stopPropagation()}>
                <Input
                    ref={searchInput}
                    placeholder={`Search ${columnName}`}
                    value={selectedKeys[0]}
                    onChange={(e: {
                        target: { value: React.Key; };
                    }) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                    onPressEnter={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                    style={{marginBottom: 12, display: 'block'}}
                />
                <div className='search-btn-row'>
                    <Button className='tbl-search-btn'
                            type="primary"
                            onClick={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                            icon={<SearchOutlined/>}
                            size="small"
                    >
                        Search
                    </Button>
                    <Button className='tbl-search-reset'
                            onClick={() => clearFilters && handleReset(clearFilters, dataIndex)}
                            size="small"
                    >
                        Reset
                    </Button>
                    <Button className='tbl-search-close'
                            size="small"
                            onClick={() => {
                                close();
                            }}
                    >
                        Close
                    </Button>
                </div>
            </div>
        ),
        filterIcon: (filtered: boolean) => (
            <SearchOutlined style={{color: filtered ? '#1677ff' : undefined}}/>
        ),
        onFilter: (value, record) =>
            record[dataIndex]
                .toString()
                .toLowerCase()
                .includes((value as string).toLowerCase()),
        onFilterDropdownOpenChange: (visible) => {
            if (visible) {
                setTimeout(() => searchInput.current?.select(), 100);
            }
        },
        render: (text) =>
            searchedColumn === dataIndex ? (
                <Highlighter
                    highlightStyle={{backgroundColor: '#ffc069', padding: 0}}
                    searchWords={[searchText]}
                    autoEscape
                    textToHighlight={text ? text.toString() : ''}
                />
            ) : (
                text
            ),
    });

    const getColor = (identificationSourceType: string) => {
        switch (identificationSourceType) {
            case IDENTIFICATION_SOURCE.NIC: {
                return 'green';
            }
            case IDENTIFICATION_SOURCE.PP: {
                return 'geekblue';
            }
            case IDENTIFICATION_SOURCE.DL: {
                return 'volcano';
            }
            default:{
                return '#8c8c8c';
            }
        }
    }

    const columns = [
        {
            title: 'No',
            dataIndex: 'key',
            render: (text: any, rec: any, index: number) => {
                return (<span>{index + 1}</span>)
            }
        },
        {
            title: 'CIN',
            dataIndex: 'cin',
            key: 'cin',
            sorter: (a: any, b: any) => {
                a = a.cin == null ? '' : a.cin
                b = b.cin == null ? '' : b.cin
                return a.localeCompare(b);
            },
            ...getColumnSearchProps('cin', 'CIN'),
            render: (text: any) => (
                treatNullValuesInTable(text)
            ),
        },
        {
            title: 'Customer Name',
            dataIndex: 'customerName',
            key: 'customerName',
            sorter: (a: any, b: any) => {
                a = a.customerName == null ? '' : a.customerName
                b = b.customerName == null ? '' : b.customerName
                return a.localeCompare(b);
            },
            ...getColumnSearchProps('customerName', 'Customer Name'),
            render: (text: any) => (
                treatNullValuesInTable(text)
            ),

        },
        {
            title: 'Identification Type',
            dataIndex: 'identificationType',
            key: 'identificationType',
            align: 'center',
            sorter: (a: any, b: any) => {
                a = a.identificationType == null ? '' : a.identificationType
                b = b.identificationType == null ? '' : b.identificationType
                return a.localeCompare(b);
            },
            ...getColumnSearchProps('identificationType', 'Identification Type'),
            render: (text: any) => {
                return (
                    <Tag
                        className={getClassName(text)}
                        color={getColor(text)} key={text}>
                        {null != text ? text.toUpperCase() : 'N/A'}
                    </Tag>
                )
            }
        },
        {
            title: 'Identification Value',
            dataIndex: 'identificationValue',
            key: 'identificationValue',
            sorter: (a: any, b: any) => {
                a = a.identificationValue == null ? '' : a.identificationValue
                b = b.identificationValue == null ? '' : b.identificationValue
                return a.localeCompare(b);
            },
            ...getColumnSearchProps('identificationValue', 'Identification Value'),
            render: (text: any) => (
                treatNullValuesInTable(text)
            ),
        },
        {
            title: 'Account',
            dataIndex: 'linkedAccount',
            key: 'linkedAccount',
            sorter: (a: any, b: any) => {
                a = a.linkedAccount == null ? '' : a.linkedAccount
                b = b.linkedAccount == null ? '' : b.linkedAccount
                return a.localeCompare(b);
            },
            ...getColumnSearchProps('linkedAccount', 'Account'),
            render: (text: any) => (
                treatNullValuesInTable(text)
            ),
        },
        {
            title: 'Nickname',
            dataIndex: 'nickname',
            key: 'nickname',
            sorter: (a: any, b: any) => {
                a = a.nickname == null ? '' : a.nickname
                b = b.nickname == null ? '' : b.nickname
                return a.localeCompare(b);
            },
            ...getColumnSearchProps('nickname', 'Nickname'),
            render: (text: any) => (
                treatNullValuesInTable(text)
            ),
        },
        {
            title: 'Request Date Time',
            dataIndex: 'requestedDate',
            key: 'requestedDate',
            sorter: (a: any, b: any) => {
                a = a.requestedDate == null ? '' : a.requestedDate
                b = b.requestedDate == null ? '' : b.requestedDate
                return a.localeCompare(b);
            },
            render: (text: any, rec: any) => (
                <span>{null != text ? dayjs(text).format(YYYY_MM_DD_HH_MM) : NOT_AVAILABLE}</span>
            )
        },

        {
            title: 'Action',
            key: 'action',
            align: "center",
            fixed: 'right' as const,
            render: (text: any, rec: IndividualAccountWrapperDto) => (
                <Space size="middle">
                    <Tooltip placement="top" title='Accept'>
                        <Button className='btn-action-outline-common btn-action-view' icon={<CheckOutlined/>}
                                onClick={() => acceptIndividualAccountRequest(rec)}>Accept</Button>
                    </Tooltip>
                    <Tooltip placement="top" title='Reject'>
                        <Button className='btn-action-outline-common btn-action-re-schedule'
                                onClick={() => showRejectModal(rec)}
                                icon={<CloseOutlined/>}>Reject</Button>
                    </Tooltip>
                </Space>
            ),
        },
    ];

    const getClassName = (text: string) => {
        if (null != text){
            return text.toUpperCase() == "DRIVING LICENSE" ? 'drl-tbl-tag' : 'tbl-tag'
        }
        return  'tbl-tag';
    }

    const showRejectModal = (record: IndividualAccountWrapperDto) => {
        setSelectedData(record)
        setRejectReasonModalOpen(true)
    }

    const treatNullValuesInTable = (value: string) => {
        if (null != value && '' != value) {
            return value;
        }
        return NOT_AVAILABLE;
    }

    const acceptIndividualAccountRequest = (record: IndividualAccountWrapperDto) => {
        modal.confirm({
            title: 'Confirm',
            icon: <ExclamationCircleOutlined/>,
            content: `Do you want to accept this individual account request?`,
            okText: 'Yes',
            className: 'confirm-dialog-box',
            cancelText: 'No',
            okButtonProps: {style: {background: '#FF7400'}},
            onOk() {
                setTableLoading(true)
                acceptRejectAccountRequest(RecordStatus.ACCEPTED, record, undefined)
            },
            onCancel() {
            }
        });
    }

    const acceptRejectAccountRequest = (type: RecordStatus, record: IndividualAccountWrapperDto, rejectReason: string | undefined) => {
        if (RecordStatus.REJECTED == type) {
            record.rejectReason = rejectReason
        }
        record.accountStatus = type
        adminService.verifyIndividualAccountRequest(record).then(res => {
            if (res.data !== null && res.data.results) {
                message.success(RecordStatus.ACCEPTED == type ? MSG_INDIVIDUAL_ACCOUNT_REQUEST_ACCEPT_SUCCESS : MSG_INDIVIDUAL_ACCOUNT_REQUEST_REJECT_SUCCESS).then();
                props.tableUpdateFunc(true);
                setTableLoading(false)
                setRejectReasonModalOpen(false)
            } else {
                message.error(RecordStatus.ACCEPTED == type ? MSG_INDIVIDUAL_ACCOUNT_REQUEST_ACCEPT_FAILURE_ERROR : MSG_INDIVIDUAL_ACCOUNT_REQUEST_REJECT_FAILURE_ERROR).then();
                setTableLoading(false)

            }
        }, (reason) => {
            ErrorHandler.handleError(reason);
            setTableLoading(false)
        })
    }

    const rejectRequest = (values: any) => {
        acceptRejectAccountRequest(RecordStatus.REJECTED, selectedData, values.rejectReason)
    }

    const handleRejectReasonCancel = (values: any) => {
        setRejectReasonModalOpen(false)
    }
    return (
        <div>
            <Table scroll={{x: 'max-content'}} columns={columns} dataSource={tableData} loading={isTableLoading}/>
            <Modal title="Reject Reason"
                   open={isRejectReasonModalOpen}
                   destroyOnClose={true}
                   maskClosable={false}
                   footer={null}
                   onCancel={handleRejectReasonCancel}
                   centered
                   className='reject-reason-modal'
            >
                <Row>
                    <Col xs={24} sm={24} md={24} lg={24} xl={24} style={{marginTop: 12}}>
                        <Form
                            layout={"vertical"}
                            initialValues={{remember: true}}
                            onFinish={rejectRequest}
                        >
                            {/*<h4>which of the following option do you prefer</h4>*/}
                            <Form.Item
                                name={"rejectReason"}
                                label={"Reject Reason"}
                                rules={[{
                                    required: true,
                                    message: 'Please enter reject reason'
                                }]}>
                                <TextArea showCount placeholder="Please enter reject reason" maxLength={255}
                                          bordered={true}/>
                            </Form.Item>
                            <Form.Item style={{marginBottom: 0}}>
                                <Space style={{float: 'right', marginTop: 16}}>
                                    <Button onClick={handleRejectReasonCancel}>
                                        Cancel
                                    </Button>
                                    <Button htmlType="submit"
                                            type="primary">
                                        Submit
                                    </Button>
                                </Space>
                            </Form.Item>


                        </Form>
                    </Col>
                </Row>

            </Modal>
        </div>


    )
};


export default IndividualAccountRequestTable;
